/**
 * "New message" (Slack's ⌘N): a To: field taking several people as chips (best matches as you type, ↑ ↓ Enter, Backspace
 * removes the last chip), then a message. Sends to the existing DM / group DM with exactly those people, or starts it.
 * With nobody picked it lists my conversations (Browse DMs, the sidebar's "More conversations…"). Renders
 * NewMessageViewModel.
 */
import type { NewMessageState, NewMessageViewModel } from "@bundle/core";
import { Avatar, Badge, Button, Icon, Pressable, Sheet, Stack, Text, TextField } from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";

export type NewMessageSheetProps = {
  open: boolean;
  onClose: () => void;
  vm: NewMessageViewModel;
  state: NewMessageState;
  /** Go to this conversation (after a send, "Open conversation" or a pick from the list). */
  onOpenConversation: (channelId: string) => void;
  online: boolean;
};

export function NewMessageSheet({ open, onClose, vm, state: s, onOpenConversation, online }: NewMessageSheetProps) {
  const t = useT();
  const tr = useTr();
  const go = (p: Promise<string | undefined>) => {
    void p.then((id) => {
      if (id !== undefined) onOpenConversation(id);
    });
  };
  const send = () => go(vm.send());
  const picking = s.recipients.length === 0;
  return (
    <Sheet open={open} onClose={onClose} title={t("newMessage.title")} closeLabel={t("common.close")} testID="new-message-sheet">
      <Stack gap="3">
        <Stack direction="horizontal" align="center" gap="1.5" wrap testID="new-message-to">
          <Text variant="footnote" weight="medium" color="text.secondary">
            {t("newMessage.to")}
          </Text>
          {s.recipients.map((m) => (
            <Pressable key={m.userId} radius="full" onPress={() => vm.remove(m.userId)} accessibilityLabel={t("primitive.chip.remove", { label: m.name })} testID={`new-message-chip-${m.userId}`}>
              <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" paddingY="1" radius="full" background="chip.bg">
                <Avatar name={m.name} src={m.avatarUrl} size="avatar.xs" square={m.isAgent} />
                <Text variant="footnote" color="chip.fg">
                  {m.name}
                </Text>
                <Icon name="close" size="icon.sm" color="text.tertiary" />
              </Stack>
            </Pressable>
          ))}
          {s.atLimit ? null : (
            <Stack grow>
              <TextField
                value={s.query}
                onChange={(v) => vm.setQuery(v)}
                placeholder={t("newMessage.toPlaceholder")}
                accessibilityLabel={t("newMessage.to")}
                variant="plain"
                autoFocus
                onKey={(key) => {
                  if (key === "up" || key === "down") {
                    vm.move(key === "up" ? -1 : 1);
                    return s.suggestions.length > 0;
                  }
                  if (key === "enter" || key === "tab") return vm.pickActive();
                  return false;
                }}
                onBackspaceEmpty={() => vm.removeLast()}
                testID="new-message-query"
              />
            </Stack>
          )}
        </Stack>
        {s.atLimit ? (
          <Text variant="caption" color="text.tertiary" testID="new-message-limit">
            {t("newMessage.limit", { max: 9 })}
          </Text>
        ) : null}

        {s.suggestions.length > 0 ? (
          <Stack gap="0.5" testID="new-message-suggestions">
            {s.suggestions.map((m, i) => (
              <Pressable key={m.userId} direction="horizontal" radius="md" selected={i === s.active} onPress={() => vm.add(m.userId)} testID={`new-message-person-${m.userId}`}>
                <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
                  <Avatar name={m.name} src={m.avatarUrl} size="avatar.xs" square={m.isAgent} />
                  <Stack grow>
                    <Text variant="footnote" lines={1}>
                      {m.name}
                    </Text>
                  </Stack>
                  {m.organization === undefined ? null : (
                    <Text variant="caption" color="external.fg" lines={1}>
                      {m.organization}
                    </Text>
                  )}
                </Stack>
              </Pressable>
            ))}
          </Stack>
        ) : null}

        {picking ? (
          <Stack gap="1" testID="new-message-conversations">
            <Text variant="footnote" weight="medium" color="text.secondary">
              {t("newMessage.recent")}
            </Text>
            {s.conversations.length === 0 ? (
              <Text variant="footnote" color="text.tertiary">
                {t("newMessage.noConversations")}
              </Text>
            ) : (
              s.conversations.map((c) => (
                <Pressable key={c.channelId} direction="horizontal" radius="md" onPress={() => onOpenConversation(c.channelId)} testID={`new-message-conversation-${c.channelId}`}>
                  <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
                    <Avatar name={c.name} size="avatar.xs" />
                    <Stack grow>
                      <Text variant="footnote" weight={c.unread ? "semibold" : undefined} lines={1}>
                        {c.name}
                      </Text>
                    </Stack>
                    {c.mentions > 0 ? <Badge count={c.mentions} /> : null}
                  </Stack>
                </Pressable>
              ))
            )}
          </Stack>
        ) : (
          <Stack gap="2">
            <Text variant="footnote" color="text.tertiary" testID="new-message-hint">
              {s.channelId === undefined ? t("newMessage.start", { name: s.title }) : t("newMessage.existing", { name: s.title })}
            </Text>
            <TextField
              value={s.draft}
              onChange={(v) => vm.setDraft(v)}
              placeholder={t("channel.composer.placeholderDm", { name: s.title })}
              accessibilityLabel={t("newMessage.title")}
              multiline
              onSubmit={send}
              testID="new-message-draft"
            />
            {s.error !== undefined ? (
              <Text variant="footnote" color="status.danger" testID="new-message-error">
                {tr(s.error)}
              </Text>
            ) : !online && s.channelId === undefined ? (
              <Text variant="footnote" color="text.tertiary" testID="new-message-offline">
                {t("newMessage.offline")}
              </Text>
            ) : null}
            <Stack direction="horizontal" align="center" justify="end" gap="2">
              <Button label={t("newMessage.open")} variant="ghost" disabled={!s.canOpen} onPress={() => go(vm.open())} testID="new-message-open" />
              <Button label={t("composer.send")} variant="primary" icon="send" loading={s.busy} disabled={!s.canSend} onPress={send} testID="new-message-send" />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Sheet>
  );
}
